import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MyDataConnectLayout from '../components/mydata/MyDataConnectLayout';
import { getMyDataDraft, patchMyDataDraft, clearMyDataDraft } from '../utils/myDataDraft';
import { nhisVerify, parseNhisError } from '../utils/nhisApi';

import kakaoIcon from '../assets/icons/providers/kakao.png';
import passIcon from '../assets/icons/providers/pass.png';
import naverIcon from '../assets/icons/providers/naver.png';
import tossIcon from '../assets/icons/providers/toss.png';

const PROVIDER_LABELS = { KAKAO: '카카오', PASS: 'PASS', NAVER: '네이버', TOSS: '토스' };
const PROVIDER_ICONS = { KAKAO: kakaoIcon, PASS: passIcon, NAVER: naverIcon, TOSS: tossIcon };

export default function MyDataGuidePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/app') ? '/app' : '';
  const draft = useMemo(() => getMyDataDraft(), []);

  const [status, setStatus] = useState('idle'); // idle | loading | error | timeout
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    if (!draft || !draft.phase || draft.phase < 'consent') {
      navigate(basePath + '/mydata/connect/consent', { replace: true });
    }
  }, [basePath, draft, navigate]);

  if (!draft || !draft.provider) return null;

  const providerLabel = PROVIDER_LABELS[draft.provider] || draft.provider;
  const providerIcon = PROVIDER_ICONS[draft.provider];

  const isLoading = status === 'loading';

  const tone = {
    surface: '#ffffff',
    surfaceMuted: '#f9fafb',
    border: '#e5e7eb',
    borderSoft: '#d1d5db',
    textStrong: '#111827',
    text: '#374151',
    textMuted: '#6b7280',
    primary: '#3498db',
    primarySoft: '#eff6ff',
  };

  const handleConfirm = async () => {
    const session = draft.nhisSession;
    if (!session) {
      // nhisSession 없으면 login 단계부터 재시작
      clearMyDataDraft();
      navigate(basePath + '/mydata/connect/consent', { replace: true });
      return;
    }

    setStatus('loading');
    setApiError('');
    try {
      const data = await nhisVerify({ session });
      const rawStdPatientId = data?.stdPatientId ?? data?.std_patient_id;
      const stdPatientId = Number(rawStdPatientId);
      if (!Number.isInteger(stdPatientId) || stdPatientId <= 0) {
        throw new Error('INVALID_STD_PATIENT_ID');
      }

      patchMyDataDraft({
        phase: 'guide',
        guidedAt: new Date().toISOString(),
        nhisSession: data.session,
        stdPatientId,
      });
      navigate(basePath + '/mydata/connect/fetching', { replace: true });
    } catch (err) {
      if (err?.message === 'INVALID_STD_PATIENT_ID') {
        setStatus('error');
        setApiError('인증 응답에서 환자 식별값(stdPatientId)을 확인하지 못했습니다. 처음부터 다시 시도해주세요.');
        return;
      }

      const parsed = parseNhisError(err);
      if (parsed.type === 'timeout') {
        setStatus('timeout');
        setApiError('응답 시간이 초과되었습니다. 인증 앱을 확인한 후 다시 시도해주세요.');
      } else if (parsed.type === 'network' || parsed.type === 'server') {
        setStatus('error');
        setApiError(parsed.message);
      } else {
        // fail / unknown: 앱에서 인증을 완료하지 않은 경우 포함
        setStatus('error');
        setApiError('인증 앱에서 인증을 완료한 후 버튼을 눌러주세요.\n인증을 완료하지 않으면 데이터를 가져올 수 없습니다.');
      }
    }
  };

  const handleRestart = () => {
    clearMyDataDraft();
    navigate(basePath + '/mydata/connect/consent', { replace: true });
  };

  return (
    <MyDataConnectLayout step={0}>
      {basePath !== '/app' && (
        <h1 className="page-title" style={{ textAlign: 'center', marginBottom: 16 }}>인증 요청을 완료해 주세요</h1>
      )}

      {/* 1단계(인증 확인) 안내 박스 */}
      <div style={{
        background: tone.surface, border: `1px solid ${tone.border}`, borderRadius: 12,
        padding: '16px 18px', marginBottom: 24,
      }}>
        <p style={{ fontSize: 15, fontWeight: 700, color: tone.textStrong, marginBottom: 12 }}>
          1단계 본인 인증 확인
        </p>
        <p style={{ margin: 0, fontSize: 13, color: tone.text, lineHeight: 1.6 }}>
          선택한 인증 서비스 앱에서 요청을 승인한 뒤 인증 완료 여부를 확인해 주세요.
        </p>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: tone.textMuted, lineHeight: 1.6 }}>
          인증이 완료되지 않은 상태에서 진행하면 조회가 실패할 수 있습니다.
        </p>
      </div>

      <div style={{ marginBottom: 32 }}>
        <div style={{
          border: `1px solid ${tone.border}`, borderRadius: 12, padding: '14px 16px',
          background: tone.surface, marginBottom: 16,
        }}>
          <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: tone.textStrong }}>
            선택한 인증 서비스
          </p>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            flexWrap: 'wrap',
          }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              {providerIcon && <img src={providerIcon} alt={providerLabel} style={{ width: 26, height: 26, objectFit: 'contain' }} />}
              <span style={{ fontWeight: 700, fontSize: 16, color: tone.textStrong }}>{providerLabel}</span>
            </div>
          </div>
        </div>

        <div style={{ border: `1px solid ${tone.border}`, borderRadius: 12, background: tone.surface, padding: '14px 16px', textAlign: 'left' }}>
          <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: tone.textStrong }}>
            진행 순서
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              `${providerLabel} 앱에서 인증 요청 메시지를 확인해 주세요.`,
              '앱에서 본인 인증을 승인해 주세요.',
              '이 화면에서 인증 확인 완료 버튼을 눌러 다음 단계로 이동해 주세요.',
            ].map((text, idx) => (
              <div
                key={text}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '22px minmax(0, 1fr)',
                  gap: 10,
                  alignItems: 'start',
                  padding: '10px 12px',
                  border: `1px solid ${tone.border}`,
                  borderRadius: 10,
                  background: tone.surfaceMuted,
                }}
              >
                <span style={{
                  width: 22, height: 22, borderRadius: 999,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700,
                  color: tone.primary, background: tone.primarySoft, border: `1px solid ${tone.borderSoft}`,
                }}>
                  {idx + 1}
                </span>
                <p style={{ margin: 0, fontSize: 13, color: tone.text, lineHeight: 1.6 }}>
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* API 오류 */}
      {apiError && (
        <div role="alert" style={{
          marginBottom: 12, padding: '12px 14px', borderRadius: 8,
          background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: 13, lineHeight: 1.6,
          whiteSpace: 'pre-line',
        }}>
          {apiError}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button type="button" className="submit-button" disabled={isLoading} onClick={handleConfirm}
          style={{ width: '100%', padding: '14px 0', fontSize: 16, opacity: isLoading ? 0.6 : 1 }}>
          {isLoading ? '확인 중...' : '인증 확인 완료'}
        </button>
        {(status === 'error' || status === 'timeout') && (
          <button type="button" onClick={handleRestart}
            style={{
              width: '100%', padding: '12px 0', fontSize: 15,
              background: tone.surfaceMuted, border: `1px solid ${tone.borderSoft}`, borderRadius: 8,
              color: tone.text, cursor: 'pointer', fontWeight: 600,
            }}>
              처음부터 다시
            </button>
        )}
      </div>
    </MyDataConnectLayout>
  );
}
